/**
 * @fileoverview Deal details page route with split layout and real data
 * @module routes/deals/$dealSlug
 */

import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { LeftSection } from '@/features/deals';
import { RightSection } from '@/features/deals/components/RightSection';
import { useApprovedProject } from '@/features/deals/hooks/useApprovedProject';
import { transformToProjectDetail, createInvestmentConstraints, transformToRoundTicketData, transformToProjectTabsData } from '@/features/deals/schemas/projectDetailTransformer';
import { useQuery } from '@/hooks/fundraiser/use-query';

/**
 * Deal details page component.
 *
 * Displays detailed information about a specific deal/project using a split layout.
 * Fetches real data from database based on SEO-friendly slug.
 *
 * Features:
 * - Split layout with fixed left sidebar and scrollable right content
 * - Real-time data fetching with TanStack Query
 * - Loading, error, and not found states
 * - Left side: 2 cards with bottom-fade borders (446px width)
 * - Right side: 4 cards with corner-gradient borders (738px width)
 *
 * Follows SOLID principles:
 * - Single Responsibility: Page composition and data fetching
 * - Open/Closed: Extensible through component props
 * - Dependency Inversion: Data fetching abstracted in hooks
 *
 * @component
 * @example
 * ```tsx
 * // Accessed via routes like:
 * // /deals/mock-project-1
 * // /deals/test-project-2
 * ```
 */
function DealDetailsPage(): ReactElement {
  const { dealSlug } = Route.useParams();

  // Fetch project data by slug
  const { data: project, isLoading, isError, error } = useApprovedProject({ slug: dealSlug });

  // Extract fundraiser address early (needed for hooks)
  const fundraiserData = project ? (Array.isArray(project.fundraisers)
    ? project.fundraisers[0]
    : project.fundraisers) : undefined;
  const fundraiserAddress = fundraiserData?.fundraiser_address as `0x${string}` | undefined;


  // DEBUG: Query smart contract data (must be called unconditionally)
  const { data: _contractQueryData } = useQuery(
    fundraiserAddress || ('0x0000000000000000000000000000000000000000' as `0x${string}`)
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center" style={{ paddingTop: '80px' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#44FF02]"></div>
          <p className="mt-4 text-white text-sm">Loading project details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center" style={{ paddingTop: '80px' }}>
        <div className="text-center max-w-md px-4">
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-8">
            <h2 className="text-red-400 text-xl font-semibold mb-2">Failed to load project</h2>
            <p className="text-onicorn-gray-400 text-sm mb-4">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (!project) {
    return (
      <div className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center" style={{ paddingTop: '80px' }}>
        <div className="text-center max-w-md px-4">
          <div className="bg-onicorn-gray-900/30 border border-onicorn-gray-500 rounded-lg p-8">
            <h2 className="text-white text-xl font-semibold mb-2">Project not found</h2>
            <p className="text-onicorn-gray-400 text-sm mb-4">
              The project you're looking for doesn't exist or is no longer available.
            </p>
            <a
              href="/deals"
              className="inline-block px-4 py-2 bg-[#44FF02] hover:bg-[#3DE002] text-black font-semibold rounded transition-colors"
            >
              Back to Deals
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is a preview deal
  const isPreview = fundraiserData?.is_preview === true;

  // Transform project data for components
  let projectDetailData, investmentConstraints, roundTicketData, projectTabsData, investmentTokenAddress;

  try {
    projectDetailData = transformToProjectDetail(project);
    investmentConstraints = createInvestmentConstraints(project);
    roundTicketData = transformToRoundTicketData(project);
    projectTabsData = transformToProjectTabsData(project);

    // Extract investment token for smart contract interactions
    investmentTokenAddress = fundraiserData?.investment_token as `0x${string}` | undefined;

  } catch (error) {
    return (
      <div className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center" style={{ paddingTop: '80px' }}>
        <div className="text-center max-w-md px-4">
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-8">
            <h2 className="text-red-400 text-xl font-semibold mb-2">Data Transformation Error</h2>
            <p className="text-onicorn-gray-400 text-sm mb-4">
              {error instanceof Error ? error.message : 'Failed to transform project data'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="relative min-h-screen bg-background overflow-hidden" style={{ paddingTop: '80px' }}>
      {/* DEBUG BUTTON - Remove in production */}
      {/*<button*/}
      {/*  onClick={handleDebugContract}*/}
      {/*  className="fixed top-24 right-4 z-50 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded shadow-lg transition-colors"*/}
      {/*>*/}
      {/*  🐛 Debug Contract Data*/}
      {/*</button>*/}

      {/* Split Layout Container - Responsive: vertical stack on mobile/tablet, horizontal split on desktop */}
      <div
        className={cn(
          // Mobile/Tablet: vertical stack, full width, content-based height
          'flex flex-col w-full px-4',
          // Tablet (≥1024px): horizontal split with smaller cards
          'lg:flex-row lg:max-w-[1400px] lg:mx-auto',
          // Desktop (≥1280px): horizontal split with full-size cards
          'xl:max-w-[1400px]'
        )}
        style={{
          height: 'auto', // Mobile/Tablet: content-based
        }}
      >
        {/* Desktop only: fixed height via media query */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (min-width: 1024px) {
              .responsive-container {
                height: calc(100vh - 80px);
              }
            }
          `
        }} />

        {/* Independent Left Scroll Area */}
        <LeftSection
          projectData={projectDetailData}
          investmentConstraints={investmentConstraints}
          fundraiserAddress={fundraiserAddress}
          investmentTokenAddress={investmentTokenAddress}
          fundraiserId={fundraiserData?.id}
          projectSlug={dealSlug}
          endTimeUnix={fundraiserData?.end_time}
          isPreview={isPreview}
          dateDisplay={isPreview ? (fundraiserData as Record<string, unknown>)?.date_display as Record<string, unknown> : undefined}
          isHiddenFromDeals={project.hidden_from_deals}
          isReferralOnly={project.referral_only}
          className="responsive-container"
        />

        {/* Gap: no gap on mobile, 24px on tablet, 36px on desktop */}
        <div className="h-0 lg:h-auto lg:w-6 xl:w-9 flex-shrink-0" />

        {/* Independent Right Scroll Area */}
        <RightSection
          roundTicketData={roundTicketData}
          projectTabsData={projectTabsData}
          projectApplicationId={project.id}
          fundraiserAddress={fundraiserAddress}
          fundraiserId={fundraiserData?.id}
          investmentTokenAddress={investmentTokenAddress}
          dateDisplay={isPreview ? (fundraiserData as Record<string, unknown>)?.date_display as Record<string, unknown> : undefined}
          isPreview={isPreview}
          isHiddenFromDeals={project.hidden_from_deals}
          className="responsive-container"
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/deals/$dealSlug')({
  component: DealDetailsPage,
});
